import React, { useCallback, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import PropTypes from 'prop-types';
import Icon from '@/components/Icon';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuthStore, useChatStore, useModalStore, useToastStore } from '@/store';
import { RESPONSE_ACTION_TYPES } from '@/constants/config';
import { exportCardImage } from '@/constants/utils/cardImageExport';

/**
 * Shared card actions: bookmark the containing message (owners only,
 * mirroring the message-actions gating) and save the card as a PNG. Rendered
 * in normal flow as a right-aligned row at the bottom of the card — absolute
 * positioning overlapped the title and clipped on narrow screens. The capture
 * target lives outside this subtree so the buttons never appear in exports.
 */
function CardActions({ conversationId, messageId, captureTargetRef, exportTitle, accentColor, borderColor, bandColor, textColor }) {
  const { t } = useTranslation();
  const { addToast } = useToastStore(state => state.actions);
  const { openModal } = useModalStore(state => state.actions);
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const isOwner = useChatStore(state => {
    if (!conversationId) return false;
    return state.chats.find(c => c.id === conversationId)?.isOwner === true;
  });
  const isSaved = useChatStore(state => {
    if (!conversationId || !messageId) return false;
    const chat = state.chats.find(c => c.id === conversationId);
    const message = chat?.messages?.find(m => m.id === messageId);
    return message?.flags?.isSaved === true;
  });

  const [isBookmarking, setIsBookmarking] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const canBookmark = !!conversationId && !!messageId && isOwner;

  const handleBookmark = useCallback(async () => {
    if (isBookmarking || !canBookmark) return;
    try {
      if (isGhost) {
        openModal('GhostSignupModal');
        return;
      }
      setIsBookmarking(true);
      if (isSaved) {
        await useChatStore.getState().unsaveMessage(conversationId, messageId);
        addToast(t('messages.unsaved'), 'success');
      } else {
        await useChatStore.getState().saveMessage(conversationId, messageId);
        addToast(t('messages.saved'), 'success', [
          {
            title: t('messages.addToFolder'),
            icon: 'folder-plus',
            action: RESPONSE_ACTION_TYPES.ADD_TO_BOOKMARK_FOLDER,
            data: { conversationId, messageId },
          },
          {
            title: t('messages.viewBookmarks'),
            icon: 'bookmark',
            href: '/bookmarks',
          },
        ]);
      }
    } catch (error) {
      console.error('Failed to toggle card bookmark:', error);
      addToast(t('messages.bookmarkFailed'), 'error');
    } finally {
      setIsBookmarking(false);
    }
  }, [addToast, canBookmark, conversationId, isBookmarking, isGhost, isSaved, messageId, openModal, t]);

  const handleSaveImage = useCallback(async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await exportCardImage(captureTargetRef.current, exportTitle, accentColor);
    } catch (error) {
      console.error('[CardActions] Save image failed:', error);
      addToast(t('messages.imageDownloadFailed'), 'error');
    } finally {
      setIsDownloading(false);
    }
  }, [addToast, accentColor, captureTargetRef, exportTitle, isDownloading, t]);

  return (
    <View style={[styles.container, { borderTopColor: borderColor }]} dataSet={{ copyExclude: '' }}>
      {canBookmark && (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={isSaved ? t('cards.bookmarked') : t('cards.bookmark')}
          disabled={isBookmarking}
          onPress={handleBookmark}
          style={({ pressed, hovered }) => [
            styles.actionButton,
            {
              backgroundColor: hovered || isSaved ? `${accentColor}1f` : 'transparent',
              opacity: pressed || isBookmarking ? 0.7 : 1,
            },
          ]}
        >
          <Icon name="bookmark" solid={isSaved} size={14} color={isSaved ? accentColor : textColor} />
          <Text style={[styles.actionText, { color: isSaved ? accentColor : textColor }]}>
            {isSaved ? t('cards.bookmarked') : t('cards.bookmark')}
          </Text>
        </Pressable>
      )}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('cards.saveImage')}
        disabled={isDownloading}
        onPress={handleSaveImage}
        style={({ pressed, hovered }) => [
          styles.actionButton,
          {
            backgroundColor: hovered ? bandColor : 'transparent',
            opacity: pressed || isDownloading ? 0.7 : 1,
          },
        ]}
      >
        <Icon name="download" size={14} color={textColor} />
        <Text style={[styles.actionText, { color: textColor }]}>
          {isDownloading ? t('cards.preparingImage') : t('cards.saveImage')}
        </Text>
      </Pressable>
    </View>
  );
}

CardActions.propTypes = {
  conversationId: PropTypes.string,
  messageId: PropTypes.string,
  captureTargetRef: PropTypes.object.isRequired,
  exportTitle: PropTypes.string.isRequired,
  accentColor: PropTypes.string.isRequired,
  borderColor: PropTypes.string.isRequired,
  bandColor: PropTypes.string.isRequired,
  textColor: PropTypes.string.isRequired,
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    alignItems: 'center',
    borderTopWidth: 1,
    paddingHorizontal: 14,
    paddingTop: 8,
    paddingBottom: 12,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 44,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginLeft: 4,
  },
  actionText: {
    fontSize: 13,
    fontWeight: '500',
    marginLeft: 7,
  },
});

export default React.memo(CardActions);
